/**
 * @graphql cc
 */

import React, { useContext } from 'react';
import gql from 'graphql-tag';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop';
import { PageCommentEditor } from '@confluence/comment-simple/entry-points/PageCommentEditor';
import type { CommentSaveHandler } from '@confluence/comment-simple/entry-points/commentSimpleTypes';
import {
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { startEditorSubExperience } from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import type { GraphQLContentStatus } from '@confluence/comments-panel-queries';
import { CommentsType } from '@confluence/comments-panel-queries';
import {
	Platform,
	ContentRepresentation,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralCommentMutation';
import { updateApolloCacheParentCallback } from '@confluence/page-comments-queries/entry-points/pageCommentUtils';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData';
import {
	CommentType,
	useCommentsDataActions,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { END } from '@confluence/navdex';
import { handlePanelSelectionAndScroll } from '@confluence/comments-panel/entry-points/commentThreadHelper';
import {
	AnalyticsSource,
	CommentType as AnalyticsCommentType,
} from '@confluence/comments-util/entry-points/analytics';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import { useUnreadCommentsActions } from '@confluence/unread-comments';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { useObjectCommentDispatchContext } from '@confluence/comment-context/entry-points/ObjectCommentContext';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState';

import type {
	ObjectAddCommentEditorCreateGeneralCommentMutation as ObjectAddCommentEditorCreateGeneralCommentMutation$data,
	ObjectAddCommentEditorCreateGeneralCommentMutationVariables as ObjectAddCommentEditorCreateGeneralCommentMutation$variables,
} from './__types__/ObjectAddCommentEditorCreateGeneralCommentMutation';

const ObjectAddCommentEditorCreateGeneralCommentMutation = gql`
	mutation ObjectAddCommentEditorCreateGeneralCommentMutation(
		$cloudId: ID!
		$input: CreateCommentInput!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on FooterComment {
					commentResolveProperties {
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			version {
				by {
					displayName
				}
				when
				number
			}
		}
	}
`;

export interface ObjectAddCommentEditorProps {
	isOffline?: boolean;
	setAutoFocusPromptRef: (value: boolean) => void;
	setIsEditorEmpty: (isEditorEmpty: boolean) => void;
	objectAddCommentContainerId: string;
}

export const ObjectAddCommentEditor = ({
	isOffline,
	setAutoFocusPromptRef,
	setIsEditorEmpty,
	objectAddCommentContainerId,
}: ObjectAddCommentEditorProps) => {
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { closeCommentContainer } = useObjectCommentDispatchContext();
	const [contentId] = usePageContentId();
	const [{ searchSessionId }] = useSearchSessionId();

	const { setCurrentlySelectedThreadKey } = useCommentsPanelActions();
	const { hasContentChanged } = useCommentsContentState();
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { addNewCommentThreads } = useCommentsDataActions();
	const { updateReadCommentsListState } = useUnreadCommentsActions();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const pageMode = useGetPageMode();
	const [contentType] = useContentType();
	const { cloudId } = useSessionData();

	const [createGeneralCommentFn] = useMutationInterop<
		ObjectAddCommentEditorCreateGeneralCommentMutation$data,
		ObjectAddCommentEditorCreateGeneralCommentMutation$variables
	>(ObjectAddCommentEditorCreateGeneralCommentMutation);

	const handleCommentsPanelSave: CommentSaveHandler = (adfObject: any, onSuccess: any) => {
		const pageId = contentId || '';
		const { adf } = adfObject;

		startEditorSubExperience({
			name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			timeout: ExperienceTimeout.EDITOR_PUBLISH,
		});

		return createGeneralCommentFn({
			variables: {
				cloudId,
				input: {
					commentBody: {
						value: JSON.stringify(adf),
						representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
					},
					commentSource: Platform.WEB,
					containerId: pageId,
				},
			},
			// @ts-ignore -- can't import type directly in update FN because it'd be circular
			update: updateApolloCacheParentCallback(
				'create',
				{
					pageId,
					contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
					type: [CommentsType.FOOTER],
				},
				isReactionsEnabled,
			),
		}).then(({ data }: any) => {
			const commentResult = data.createFooterComment;
			const comment: CommentData = {
				...commentResult,
				replies: [], // not included in the payload
				isUnread: false,
				isOpen: true,
				type: CommentType.GENERAL,
			};

			updateReadCommentsListState((prev) => new Set([...prev, comment.id]));
			addNewCommentThreads({ inline: {}, general: { [comment.id]: comment } });

			onSuccess && onSuccess();
			resetContentChanged();
			closeCommentContainer();

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'created',
					actionSubject: 'comment',
					actionSubjectId: comment.id, // The newly created comment ID
					objectType: contentType,
					objectId: contentId,
					source: AnalyticsSource.PAGE_FOOTER,
					attributes: {
						commentType: AnalyticsCommentType.PAGE,
						source: AnalyticsSource.PAGE_FOOTER,
						pageMode,
						isReply: false,
						pageType: contentType,
						isLivePage: pageMode === PageMode.LIVE,
						editor: 'v2',
						searchSessionId,
						navdexPointType: END,
						parentCommentId: null, // analytics event schema type expects string or null
					},
				},
			}).fire();

			experienceTracker.succeed({
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			});

			setTimeout(() => {
				handlePanelSelectionAndScroll({
					threadKey: comment.id,
					isResolved: false,
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: undefined,
					commentType: CommentType.GENERAL,
					scrollCommentsPanelToThread,
					shouldScrollCommentsPanel: true,
				});
			}, 1000);
		});
	};

	const handleCancel = ({ isEditorAborted }: { isEditorAborted: boolean }) => {
		const doCancel = () => {
			if (isEditorAborted) {
				setAutoFocusPromptRef(true);
				experienceTracker.abort({
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					reason: 'aborted because comment was cancelled',
				});
			}

			closeCommentContainer();
		};

		if (hasContentChanged) {
			resetContentChanged();
		}
		doCancel();
	};

	const handleOnEditorLoaded = () => {
		experienceTracker.succeed({
			name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
		});

		const footerCommentContainer = document.getElementById(objectAddCommentContainerId);

		if (!footerCommentContainer) {
			return;
		}

		footerCommentContainer.scrollIntoView({
			behavior: 'smooth',
			block: 'end',
			inline: 'nearest',
		});
	};

	const handleEditorContentChanged = (content: any) => {
		setIsEditorEmpty(!content);
		// Also call the original onChange handler
		onChange(content);
	};

	return (
		<PageCommentEditor
			mode="create"
			saveHandler={handleCommentsPanelSave}
			cancelHandler={handleCancel}
			onEditorLoaded={handleOnEditorLoaded}
			onEditorContentChanged={handleEditorContentChanged}
			useNewExperienceAbort
			expandEditor
			isFooterComment
			isOffline={isOffline}
		/>
	);
};
